import React from "react";
import Seo from "@/components/Seo";
import { Eyebrow, SectionHeading } from "@/components/ui-bits";
import { COMPANY } from "@/data/site";
import presentation from "@/assets/brand/team-presentation.png";
import briefing from "@/assets/brand/team-briefing.png";
import handshake2 from "@/assets/brand/team-handshake-2.png";
import leadershipInstaller from "@/assets/brand/leadership-installer.png";
import warehouseTruck from "@/assets/brand/team/about-1-warehouse-truck.png";
import dynessExpo from "@/assets/brand/team/about-2-dyness-expo.png";
import briefingRoom from "@/assets/brand/team/about-3-briefing-room.png";
import teamGroup from "@/assets/brand/team/about-4-team-group.png";
import installHandshake from "@/assets/brand/team/about-5-install-handshake.png";
import siteClients from "@/assets/brand/team/about-6-site-clients.png";
import siteTechQuestions from "@/assets/brand/team/about-7-site-tech-questions.png";
import siteCommercial from "@/assets/brand/team/about-8-site-commercial.png";
import siteDynessWalkthrough from "@/assets/brand/team/about-9-site-dyness-walkthrough.png";
import siteCrewDebrief from "@/assets/brand/team/about-10-site-crew-debrief.png";
import { PhotoCarousel } from "@/components/PhotoCarousel";
import { ReviewsSection } from "@/components/Reviews";

export default function About() {
  return (
    <>
      <Seo title="About — EI Battery Solutions" description="EI Battery Solutions is an Australia-wide wholesale supplier of batteries and selected hybrid inverters to registered electrical contractors, builders and property developers." />
      <section className="bg-ei-navy text-ei-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <Eyebrow className="text-ei-gold">About us</Eyebrow>
          <h1 className="mt-4 text-4xl sm:text-5xl font-bold tracking-tight max-w-3xl">A wholesale supply partner for the trade</h1>
          <p className="mt-5 text-lg text-ei-white/75 max-w-2xl leading-relaxed">
            EI Battery Solutions supplies batteries, hybrid inverters and solar panels to registered Australian
            electrical contractors, builders and property developers approved for a trade account. We are
            supply-only — your appropriately licensed contractors and accredited installers own the design,
            installation and maintenance.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-24 grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-6">
          <SectionHeading eyebrow="Our approach" title="Clear boundaries, dependable supply" />
          <div className="mt-6 space-y-4 text-ei-graphite/90 leading-relaxed">
            <p>
              We supply registered electrical contractors, and builders and property developers approved by EI
              Battery for a trade account. Builder and developer accounts must nominate the appropriately licensed
              electrical contractor responsible for installation; a trade account does not authorise electrical work.
              The contractor designs and installs each system; we supply the equipment and support the installation
              team throughout.
            </p>
            <p>
              Dyness provides the applicable manufacturer warranty for eligible Dyness products, and EI Battery does
              not offer an additional voluntary supplier product warranty. End users usually contact their installer
              first; we then assist the trade customer and contractor to lodge, document and follow up the claim with
              Dyness. This does not prevent anyone exercising rights that cannot be excluded under law.
            </p>
            <p>
              Our launch range is offered as a market-leading wholesale proposition, with Australia-wide delivery and a
              straightforward enquiry-to-collection process.
            </p>
          </div>
        </div>
        <div className="lg:col-span-6">
          <div className="rounded-md overflow-hidden border border-ei-mist">
            <img src={presentation} alt="EI Battery Solutions presenting to trade customers" className="w-full object-cover" loading="lazy" />
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { src: briefing, alt: "EI Battery Solutions team briefing electrical contractors" },
            { src: handshake2, alt: "EI Battery Solutions staff with trade customers" },
            { src: leadershipInstaller, alt: "EI Battery Solutions leadership with an installer" },
          ].map((img) => (
            <div key={img.alt} className="rounded-md overflow-hidden border border-ei-mist aspect-[4/3]">
              <img src={img.src} alt={img.alt} className="w-full h-full object-cover" loading="lazy" />
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14" data-testid="about-photo-gallery">
        <SectionHeading eyebrow="Inside EI Battery" title="Our team, warehouse and partners" />
        <div className="mt-8">
          <PhotoCarousel
            testId="about-site-carousel"
            slides={[
              { src: siteClients, caption: "Meeting our clients out on site" },
              { src: siteTechQuestions, caption: "Out onsite with clients and trades going over tech questions" },
              { src: siteCommercial, caption: "On site with the trades, working through the install" },
              { src: siteDynessWalkthrough, caption: "Walking the crew through a twin Dyness and Solis switchboard setup" },
              { src: siteCrewDebrief, caption: "Wrapping up with the crew after a twin battery commissioning" },
            ]}
          />
        </div>
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
          {[
            { src: warehouseTruck, alt: "EI Battery Solutions staff at the warehouse loading dock with a delivery trailer", caption: "Making orders happen", span: "lg:col-span-4" },
            { src: dynessExpo, alt: "EI Battery Solutions at the Dyness energy storage expo stand", caption: "At the national convention conference for Dyness", span: "lg:col-span-2" },
            { src: briefingRoom, alt: "EI Battery Solutions team reviewing Dyness Stack100 capacity options", caption: "Going over products with the Dyness team", span: "lg:col-span-3" },
            { src: teamGroup, alt: "EI Battery Solutions leadership with the wider team", caption: "Our team, start of the day", span: "lg:col-span-3" },
            { src: installHandshake, alt: "EI Battery Solutions staff with a licensed contractor at a completed battery installation", caption: "Meeting our clients out on site", span: "lg:col-span-6" },
          ].map((img) => (
            <figure key={img.alt} className={`${img.span} rounded-md overflow-hidden border border-ei-mist bg-white`}>
              <img src={img.src} alt={img.alt} className="w-full h-full object-cover" loading="lazy" />
              <figcaption className="px-4 py-3 text-sm text-ei-graphite/80">{img.caption}</figcaption>
            </figure>
          ))}
        </div>
      </section>

      <section className="bg-white border-t border-ei-mist">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
          <SectionHeading eyebrow="Leadership" title="Who you'll deal with" />
          <div className="mt-10 grid sm:grid-cols-2 gap-6 max-w-3xl">
            {[
              { name: COMPANY.ceo, role: "Chief Executive Officer", body: "Sets commercial policy and approves company commitments." },
              { name: COMPANY.headOfSales, role: "Head of Sales", body: "Owns lead response, quotations and follow-up for trade customers.", photo: "/media/owner-supplied/charles-gardiner.png" },
            ].map((p) => (
              <div key={p.name} className="border border-ei-mist rounded-md p-6 sm:p-8 bg-ei-white">
                <div className="ei-eyebrow">{p.role}</div>
                <h3 className="mt-3 text-xl font-semibold text-ei-navy">{p.name}</h3>
                <p className="mt-2 text-sm text-ei-graphite/85 leading-relaxed">{p.body}</p>
                {p.photo && <img src={p.photo} alt={p.name} width="1254" height="1254" className="mt-6 w-full max-w-[260px] h-auto object-contain rounded-sm" loading="lazy" data-testid="charles-gardiner-portrait"/>}
              </div>
            ))}
          </div>
          <p className="mt-8 text-xs text-ei-graphite/60 max-w-3xl border-l-2 border-ei-gold/60 pl-4 leading-relaxed">
            For review: company history, credentials, sales volumes, legal entity, ABN and business address are not
            fabricated and remain pending confirmation before publication.
          </p>
        </div>
      </section>

      <section className="bg-ei-navy text-ei-white border-t border-ei-mist" data-testid="about-contractor-testimonials">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
          <Eyebrow className="text-ei-gold">Trade feedback</Eyebrow>
          <h2 className="mt-4 text-base md:text-lg font-semibold tracking-tight">What our contractors have to say</h2>
          <div className="mt-8 grid gap-8 lg:grid-cols-2">
            {[
              {
                id: "ben-myers",
                title: "Outstanding service from start to finish",
                name: "Ben Myers",
                company: "Lights On Electrical",
                body: [
                  "Dealing with EI Battery Solutions has been an absolute pleasure. As a contractor, it's refreshing to work with a company that makes the entire process so straightforward and stress-free. From our initial enquiry right through to delivery, the team was incredibly attentive, professional and always available to answer questions.",
                  "Craig's knowledge of battery systems and solar technology is truly remarkable. His expertise, attention to detail and willingness to go above and beyond gave us complete confidence that we were getting the right products for the job.",
                  "Communication was excellent, everything ran smoothly, and nothing was ever too much trouble. It's clear these guys genuinely care about their customers and take pride in what they do.",
                  "I wouldn't hesitate to recommend EI Battery Solutions to any contractor or installer looking for quality products, expert advice and outstanding service. We'll definitely be working with them again!",
                ],
              },
              {
                id: "shawn-moss",
                title: "Exceptional service, knowledge and reliability",
                name: "Shawn Moss",
                company: "Goodwill Energy",
                body: [
                  "Working with EI Battery Solutions has been a fantastic experience. From the very first conversation, the team made everything easy, providing exceptional customer service, clear communication and genuine support throughout the entire process.",
                  "What really sets EI Battery Solutions apart is their attention to detail and commitment to making sure everything is done right. Craig's extensive industry knowledge and understanding of battery storage solutions is second to none. He takes the time to explain everything properly, offers practical advice and genuinely understands what contractors and installers need.",
                  "The whole experience was seamless, from selecting the right equipment through to organising supply and delivery. It's great dealing with a company that values relationships and stands behind its products.",
                  "I highly recommend EI Battery Solutions to anyone in the solar and electrical industry. Professional, knowledgeable, reliable and an absolute pleasure to deal with. Looking forward to continuing our partnership!",
                ],
              },
            ].map((t) => (
              <figure key={t.id} className="border-l-2 border-ei-gold/70 pl-5 sm:pl-8" data-testid={`testimonial-${t.id}`}>
                <div className="text-ei-gold tracking-widest text-sm" aria-label="5 out of 5 stars">★★★★★</div>
                <figcaption className="mt-3 font-display text-xl sm:text-2xl font-bold text-ei-white">{t.title}</figcaption>
                <blockquote className="mt-5 space-y-4 text-ei-white/80 leading-relaxed">
                  {t.body.map((para, i) => <p key={i}>{para}</p>)}
                </blockquote>
                <div className="mt-6 text-sm">
                  <span className="font-semibold text-ei-white">{t.name}</span>
                  <span className="block text-ei-white/60">{t.company}</span>
                </div>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-ei-white border-t border-ei-mist">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
          <ReviewsSection />
        </div>
      </section>
    </>
  );
}
